import forEach from "lodash/forEach";
import groupBy from "lodash/groupBy";
import uniqueId from "lodash/uniqueId";
import {
  FIELD_SUBTEMPLATE_KEY,
  FORM_ROLES_KEY,
  FORM_SUBTEMPLATES_KEY,
  NO_SUBTEMPLATE_VALUE
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { TemplateKey } from "PFApp/constants/templates";
import { useInitialRoleData } from "PFApp/hooks/activity_form/multi_roles_form/use_initial_role_data";
import { useMultiActivityFormStoreActions } from "PFApp/stores/activity_form/multi_roles_form";
import { MultiActivityForm } from "PFApp/types/activity_form/activity_form_schema";
import { LoadingDots } from "PFComponents/loading_dots";
import { useSubtemplatesDefaultAPICustomValues } from "PFCore/hooks/queries/subtemplates/use_subtemplates_default_api_custom_values";
import { Activity } from "PFCore/types";
import { PropsWithChildren, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";

type MultiActivityFormProviderProps = PropsWithChildren<{
  initialActivities: Activity[];
  isEachInitialActivityNew: boolean;
  fieldsBlacklist?: string[];
  forceInterestQuestionsFromActivity?: boolean;
}>;

const MultiActivityFormProviderContent = ({
  initialActivities,
  isEachInitialActivityNew,
  fieldsBlacklist,
  forceInterestQuestionsFromActivity,
  children
}: MultiActivityFormProviderProps) => {
  const { setRoleData } = useMultiActivityFormStoreActions();
  const { getInitialRoleData } = useInitialRoleData({ fieldsBlacklist });
  const activitiesBySubtemplateEntries = Object.entries(
    groupBy(initialActivities, (activity) => activity.subtemplate_key || NO_SUBTEMPLATE_VALUE)
  );

  const methods = useForm<MultiActivityForm>({
    reValidateMode: "onSubmit",
    defaultValues: {
      [FORM_SUBTEMPLATES_KEY]: activitiesBySubtemplateEntries.map(([subtemplateKey, activities]) => ({
        [FIELD_SUBTEMPLATE_KEY]: subtemplateKey,
        [FORM_ROLES_KEY]: activities.map((activity) =>
          getInitialRoleData({
            uniqueId: uniqueId(),
            subtemplateKey,
            isNew: isEachInitialActivityNew,
            activity,
            forceInterestQuestionsFromActivity
          })
        )
      }))
    }
  });

  useEffect(() => {
    const formValues = methods.getValues();
    forEach(formValues[FORM_SUBTEMPLATES_KEY], (subtemplate, subtemplateIndex) => {
      forEach(subtemplate[FORM_ROLES_KEY], (role, roleIndex) => {
        const activity = activitiesBySubtemplateEntries[subtemplateIndex][1][roleIndex];
        setRoleData(role.uniqueId, {
          activityCreator: isEachInitialActivityNew ? null : activity.profile,
          attachments: []
        });
      });
    });
  }, []);

  return <FormProvider {...methods}>{children}</FormProvider>;
};

export const MultiActivityFormProvider = (props: MultiActivityFormProviderProps) => {
  const { isFetched } = useSubtemplatesDefaultAPICustomValues({
    templateKey: TemplateKey.Role
  });

  if (!isFetched) {
    return <LoadingDots circlesEnabled circleSize="md" centered pageViewEnabled />;
  }

  return <MultiActivityFormProviderContent {...props} />;
};
